import { newsletterPostsApi } from "@circle-react/api/newsletterPostsApi";
import { reactQueryGet } from "@circle-react/helpers/backendRequestHelpers";
import { isPositiveInt } from "@circle-react/helpers/number";
import { internalApi } from "@circle-react/helpers/urlHelpers";
import { queryClient } from "@circle-react/providers/QueryClient/reactQueryConfig";
import type {
  NewsletterPost,
  NewsletterPostReaderResponse,
  NewsletterPostStatus,
  NewsletterPostsListResponse,
} from "@circle-react/types/Newsletter";
import { queryOptions } from "react-query";

import { newsletterPostKey, newslettersKey } from "./newsletter";

const POST_DETAIL_STALE_TIME = 10 * 60 * 1000; // 10 minutes
const POSTS_LIST_PER_PAGE = 20;

interface ListPageParam {
  pageParam?: number;
}

export const newsletterPostQueries = {
  list: (
    newsletterId: number,
    status?: NewsletterPostStatus,
    query?: string,
  ) => ({
    initialPageParam: 1,
    queryKey: newsletterPostKey.key(
      newsletterId,
      "list",
      status ?? "all",
      query ?? "",
    ),
    queryFn: ({ pageParam = 1 }: ListPageParam) =>
      newsletterPostsApi.list({
        newsletterId,
        params: {
          page: pageParam,
          per_page: POSTS_LIST_PER_PAGE,
          ...(status && { status }),
          ...(query && { query }),
        },
      }),
    getNextPageParam: (lastPage: NewsletterPostsListResponse) =>
      lastPage.has_next_page ? lastPage.page + 1 : undefined,
    getPreviousPageParam: (firstPage: NewsletterPostsListResponse) =>
      firstPage.page > 1 ? firstPage.page - 1 : undefined,
  }),

  detail: (newsletterId: number | undefined, id: number) =>
    queryOptions({
      queryKey: newsletterPostKey.key(newsletterId ?? 0, "detail", id),
      queryFn: () => {
        if (!newsletterId) throw new Error("Newsletter ID is required.");

        return newsletterPostsApi.show({ newsletterId, id });
      },
      enabled: isPositiveInt(newsletterId) && isPositiveInt(id),
      staleTime: POST_DETAIL_STALE_TIME,
    }),

  reader: (slug: string) =>
    queryOptions({
      queryKey: newslettersKey.key("posts", "reader", slug),
      queryFn: () =>
        reactQueryGet<NewsletterPostReaderResponse>(
          internalApi.newsletterPosts.view({ slug }),
        ),
      enabled: Boolean(slug),
    }),
};

export const newsletterPostCache = {
  setDetail: (newsletterId: number, id: number, post: NewsletterPost) =>
    queryClient.setQueryData(
      newsletterPostQueries.detail(newsletterId, id).queryKey,
      post,
    ),
  getDetail: (newsletterId: number, id: number) =>
    queryClient.getQueryData<NewsletterPost>(
      newsletterPostQueries.detail(newsletterId, id).queryKey,
    ),
};

export const newsletterPostInvalidations = {
  all: (newsletterId: number) =>
    queryClient.invalidateQueries({
      queryKey: newsletterPostKey.key(newsletterId),
    }),
  list: (newsletterId: number) =>
    queryClient.invalidateQueries({
      queryKey: newsletterPostKey.key(newsletterId, "list"),
    }),
  detail: (newsletterId: number, id: number) =>
    queryClient.invalidateQueries({
      queryKey: newsletterPostKey.key(newsletterId, "detail", id),
    }),
  reader: (slug: string) =>
    queryClient.invalidateQueries({
      queryKey: newsletterPostQueries.reader(slug).queryKey,
    }),
};
